import React from 'react';
import type { TextInput } from 'react-native';
import { plural, t } from '@lingui/macro';

import type { Icon as IconType } from '@rover/icons';
import { Check } from '@rover/icons';
import { DSTokenMap } from '@rover/kibble/styles';

import { Box, Flex, Text } from '../../../core';
import { SpacingSystemType } from '../../../core/styledSystemPropTypes';
import { ControlSide, KibbleFormFieldComponentCommonProps } from '../../types';
import { SharedTypographyProps } from '../sharedTypography';

export type TextareaMinHeightType = string | [string, string] | [string, string, string];

type DefaultVariantOptions = {
  /** Displays a character count message below the field.
   *  Should be passed a `useKibbleForm().watch('key').length`,
   *  this is kinda awk but gives the best form performance */
  currentCharacterCount?: number;
};

type TextareaVariantOptions = {
  minHeight?: TextareaMinHeightType;
  /** Displays a word count message below the field.
   * Requires you to also pass currentWordCount */
  minimumWordCount?: number;
  /** Displays a word count message below the field.
   * Should be passed a `getWordCount(useKibbleForm().watch('key'))`,
   * this is kinda awk but gives the best form performance */
  currentWordCount?: number;
};

export type VariantProps =
  | {
      variant?: 'default';
      variantOptions?: DefaultVariantOptions;
    }
  | {
      variant: 'textarea';
      variantOptions?: TextareaVariantOptions;
    };

const isDefaultVariantOptions = (
  variantOptions: VariantProps['variantOptions']
): variantOptions is DefaultVariantOptions => {
  return (variantOptions as DefaultVariantOptions).currentCharacterCount !== undefined;
};

export const isTextareaVariantOptions = (
  variantOptions: VariantProps['variantOptions']
): variantOptions is TextareaVariantOptions => {
  const opts = variantOptions as TextareaVariantOptions;
  return !!(opts.minimumWordCount || opts.currentWordCount || opts.minHeight);
};

const characterAndWordCountCommonTextStyles = {
  mt: '1x',
  size: '100',
  textAlign: 'right',
  textColor: 'tertiary',
} as const;

type CharacterAndWordCountProps = {
  variant: VariantProps['variant'];
  variantOptions: VariantProps['variantOptions'];
};

export type TextFieldCommonProps = KibbleFormFieldComponentCommonProps &
  SharedTypographyProps &
  VariantProps & {
    fieldWidth?: string;
    icon?: IconType;
    iconAlignment?: ControlSide;
    placeholder?: string;
    readOnly?: boolean;
    translatedFieldErrorMessage?: string;
    webInputRef?: React.RefCallback<HTMLElement>;
    rxnInputRef?: React.RefCallback<TextInput>;
    marginBottom?: SpacingSystemType;
  };

export const getBorderColor = (hasError?: boolean, disabled?: boolean): string => {
  if (hasError) return DSTokenMap.BORDER_COLOR_ERROR.toString();
  if (disabled) return DSTokenMap.INTERACTIVE_LINK_COLOR_SECONDARY_INVERSE_HOVER.toString();

  return DSTokenMap.INTERACTIVE_BORDER_COLOR_PRIMARY.toString();
};

export const getBackgroundColor = (disabled?: boolean, readOnly?: boolean): string => {
  if (disabled) return DSTokenMap.INTERACTIVE_BORDER_COLOR_DISABLED.toString();
  if (readOnly) return DSTokenMap.INTERACTIVE_BACKGROUND_COLOR_DISABLED.toString();

  return DSTokenMap.INTERACTIVE_BACKGROUND_COLOR_PRIMARY.toString();
};

export interface TextFieldIconProps {
  icon: IconType;
  iconAlignment: NonNullable<TextFieldCommonProps['iconAlignment']>;
  disabled?: boolean;
}

export const TextFieldIcon = ({
  icon: Icon,
  iconAlignment,
  disabled,
}: TextFieldIconProps): JSX.Element | null => {
  if (!Icon) return null;

  const testingID = process.env.JS_ENV_NATIVE ? 'testID' : 'data-testid';
  const flexProps = {
    alignItems: 'center',
    [testingID]: 'kff-text-field-icon',
    position: 'absolute' as const,
    [iconAlignment]: '2x',
  };
  const iconColor = disabled
    ? DSTokenMap.INTERACTIVE_TEXT_COLOR_DISABLED
    : DSTokenMap.TEXT_COLOR_SECONDARY;

  return (
    <Flex {...flexProps}>
      <Icon height="24px" width="24px" aria-hidden fill={iconColor.toString()} />
    </Flex>
  );
};

export const CharacterCount = ({
  variant,
  variantOptions,
}: CharacterAndWordCountProps): JSX.Element | null => {
  if (
    variant === 'default' &&
    isDefaultVariantOptions(variantOptions) && // kinda silly, but TS needs this to refine variantOptions
    variantOptions.currentCharacterCount !== undefined // 0 is falsy, so we need to check "not undefined"
  ) {
    return (
      <Text {...characterAndWordCountCommonTextStyles} data-testid="character-count-message">
        {variantOptions.currentCharacterCount}{' '}
        {plural(variantOptions.currentCharacterCount, {
          one: 'character',
          other: 'characters',
        })}
      </Text>
    );
  }
  return null;
};

export const WordCount = ({
  variant,
  variantOptions,
}: CharacterAndWordCountProps): JSX.Element | null => {
  if (
    variant === 'textarea' &&
    isTextareaVariantOptions(variantOptions) && // kinda silly, but TS needs this to refine variantOptions
    variantOptions.minimumWordCount &&
    variantOptions.currentWordCount !== undefined // 0 is falsy, so we need to check "not undefined"
  ) {
    const numWordsLeft = variantOptions.minimumWordCount - variantOptions.currentWordCount;

    if (numWordsLeft < 1) {
      return (
        <Flex
          flexDirection="row"
          alignItems="center"
          justifyContent="flex-end"
          mt="1x"
          data-testid="word-count-message-check"
        >
          <Box
            as={Check}
            height="16px"
            width="16px"
            sx={{
              fill: 'text.success',
              stroke: 'text.success',
              strokeWidth: '4px',
            }}
          />
        </Flex>
      );
    }
    return (
      <Text {...characterAndWordCountCommonTextStyles} data-testid="word-count-message">
        {numWordsLeft === variantOptions.minimumWordCount
          ? t`${numWordsLeft} words minimum`
          : plural(numWordsLeft, {
              one: '# more word needed',
              other: '# more words needed',
            })}
      </Text>
    );
  }
  return null;
};
